import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from './LanguageToggle';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.services'), href: '/services' },
    { name: 'Gallery', href: '/gallery', hasDropdown: true },
    { name: t('nav.appointment'), href: '/appointment' },
    { name: t('nav.contact'), href: '/contact' },
  ];

  const galleryCategories = [
    { name: 'Hospital Facilities', href: '/gallery/facilities' },
    { name: 'Medical Equipment', href: '/gallery/equipment' },
    { name: 'Staff & Team', href: '/gallery/staff' },
    { name: 'Events & Activities', href: '/gallery/events' },
    { name: 'Patient Care', href: '/gallery/patient-care' }
  ];
  const isActive = (href: string) => location.pathname === href;

  return (
    <header className="bg-white shadow-lg sticky top-0 z-40">
      <div className="container-max">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/RABINISIA LOGO.jpg" 
              alt="Rabininsia Memorial Hospital Logo" 
              className="h-12 w-12 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Rabininsia Memorial Hospital</h1>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-4 xl:space-x-8">
            {navigation.map((item) => (
              <div key={item.name} className="relative">
                {item.hasDropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setIsGalleryOpen(true)}
                    onMouseLeave={() => setIsGalleryOpen(false)}
                  >
                    <button
                      className={`flex items-center text-xs xl:text-sm font-medium transition-colors ${
                        isActive(item.href) || location.pathname.startsWith('/gallery')
                          ? 'text-blue-600 border-b-2 border-blue-600 pb-1'
                          : 'text-gray-700 hover:text-blue-600'
                      }`}
                    >
                      {item.name}
                      <ChevronDown className="ml-1 h-3 w-3" />
                    </button>
                    
                    {isGalleryOpen && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-2 z-50">
                        <Link
                          to="/gallery"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600"
                        >
                          All Gallery
                        </Link>
                        {galleryCategories.map((category) => (
                          <Link
                            key={category.name}
                            to={category.href}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600"
                          >
                            {category.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`text-xs xl:text-sm font-medium transition-colors ${
                      isActive(item.href)
                        ? 'text-blue-600 border-b-2 border-blue-600 pb-1'
                        : 'text-gray-700 hover:text-blue-600'
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Emergency Contact */}
          <div className="hidden xl:flex items-center space-x-4">
            <LanguageToggle />
            {/* <a
              href="/admin-panel"
              className="text-sm text-gray-600 hover:text-blue-600 transition-colors"
            >
              Admin
            </a> */}
            <div className="text-right hidden xl:block">
              <div className="text-sm font-medium text-red-600">{t('nav.emergency')}</div>
              <div className="text-sm text-gray-600">+255 658 849 670</div>
            </div>
            <div className="lg:block xl:hidden">
              <LanguageToggle />
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200">
            <div className="py-4 space-y-2">
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.hasDropdown ? (
                    <div>
                      <Link
                        to="/gallery"
                        onClick={() => setIsMenuOpen(false)}
                        className={`block px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                          location.pathname.startsWith('/gallery')
                            ? 'text-blue-600 bg-blue-50'
                            : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                        }`}
                      >
                        {item.name}
                      </Link>
                      <div className="ml-4 mt-1 space-y-1">
                        {galleryCategories.map((category) => (
                          <Link
                            key={category.name}
                            to={category.href}
                            onClick={() => setIsMenuOpen(false)}
                            className="block px-4 py-1 text-xs text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded"
                          >
                            {category.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      className={`block px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive(item.href)
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <div className="px-4 py-2 border-t border-gray-200 mt-4">
                <LanguageToggle />
              </div>
              <div className="px-4 py-2">
                <div className="text-sm font-medium text-red-600">{t('nav.emergency')}</div>
                <div className="text-sm text-gray-600">+255 658 849 670</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;